import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormSection, Button, DeleteConfirmationModal } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import {
  NotifyContactFields,
  NotifyInternalFields,
  ProjectIdentityFields,
  ProjectMembersField,
} from './form/shared';
import { useUpdateProject, useDeleteProject } from '../hooks';
import {
  PROJECT_DEFAULT_MESSAGE_TEMPLATE,
  type ProjectResponse,
  type ProjectInternalNotifyConfig,
  type ProjectNotifyEvents,
  type ProjectNotifyConfig,
} from '../types';

export interface ProjectConfigTabProps {
  project: ProjectResponse;
}

interface IdentityState {
  name: string;
  clientId: string;
  color: string;
  pipelineId: string;
  leaderId: string;
}
interface NotifyState {
  contactIds: string[];
  channelId?: string;
  events: ProjectNotifyEvents;
  messageTemplate: string;
}

/** Rodapé "Salvar" de uma seção de config (padrão salvar por seção). */
const SaveRow: React.FC<{ onSave: () => void; loading: boolean; testId: string }> = ({ onSave, loading, testId }) => (
  <div className="flex justify-end mt-4 pt-4 border-t" style={{ borderColor: 'var(--theme-card-border)' }}>
    <Button variant="secondary" size="sm" icon="save" onClick={onSave} loading={loading} data-testid={testId}>
      Salvar
    </Button>
  </div>
);

/**
 * Aba "Configurações" do workspace (protótipo G9). Grid de seções com salvar
 * POR SEÇÃO (padrão AccountPage) reutilizando os campos compartilhados da Task
 * 12. Inclui a Zona de perigo (arquivar/reativar + excluir).
 */
export const ProjectConfigTab: React.FC<ProjectConfigTabProps> = ({ project }) => {
  const navigate = useNavigate();

  // Uma instância de mutação por seção → estados de loading independentes.
  const saveNotify = useUpdateProject();
  const saveInternalMut = useUpdateProject();
  const saveIdentity = useUpdateProject();
  const saveMembersMut = useUpdateProject();
  const saveStatus = useUpdateProject();
  const deleteProject = useDeleteProject();

  const [identity, setIdentity] = useState<IdentityState>({
    name: '',
    clientId: '',
    color: '#8b5cf6',
    pipelineId: '',
    leaderId: '',
  });
  const [members, setMembers] = useState<string[]>([]);
  const [notify, setNotify] = useState<NotifyState>({
    contactIds: [],
    events: { milestoneCompleted: true, phaseCompleted: true, projectCompleted: true, taskCompleted: false },
    messageTemplate: PROJECT_DEFAULT_MESSAGE_TEMPLATE,
  });
  const [internal, setInternal] = useState<ProjectInternalNotifyConfig>({
    unblocked: true,
    completedLeader: true,
    overdue: true,
  });
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Seed do estado local a partir do projeto (apenas quando muda de projeto).
  useEffect(() => {
    setIdentity({
      name: project.name ?? '',
      clientId: project.clientId ?? '',
      color: project.color ?? '#8b5cf6',
      pipelineId: project.pipelineId ?? '',
      leaderId: project.leaderId ?? '',
    });
    setMembers(project.memberIds ?? []);
    setNotify({
      contactIds: project.contactIds ?? [],
      ...(project.notifyConfig?.channelId ? { channelId: project.notifyConfig.channelId } : {}),
      events: project.notifyConfig?.events ?? {
        milestoneCompleted: true,
        phaseCompleted: true,
        projectCompleted: true,
        taskCompleted: false,
      },
      messageTemplate: project.notifyConfig?.messageTemplate ?? PROJECT_DEFAULT_MESSAGE_TEMPLATE,
    });
    setInternal(
      project.internalNotifyConfig ?? { unblocked: true, completedLeader: true, overdue: true },
    );
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isArchived = project.status === 'archived';

  const handleSaveNotify = () => {
    const notifyConfig: ProjectNotifyConfig = {
      events: notify.events,
      messageTemplate: notify.messageTemplate,
      ...(notify.channelId ? { channelId: notify.channelId } : {}),
    };
    saveNotify.mutate({ id: project.id, data: { contactIds: notify.contactIds, notifyConfig } });
  };

  const handleSaveInternal = () => {
    saveInternalMut.mutate({ id: project.id, data: { internalNotifyConfig: internal } });
  };

  const handleSaveIdentity = () => {
    saveIdentity.mutate({
      id: project.id,
      data: {
        name: identity.name.trim(),
        // string vazia = opção "— Sem cliente —" (Zod aceita e o repo faz $unset).
        clientId: identity.clientId,
        color: identity.color,
        pipelineId: identity.pipelineId,
        leaderId: identity.leaderId,
        // Garante que o líder não permaneça na lista de membros.
        memberIds: members.filter((m) => m !== identity.leaderId),
      },
    });
  };

  const handleSaveMembers = () => {
    saveMembersMut.mutate({ id: project.id, data: { memberIds: members } });
  };

  const handleToggleStatus = () => {
    saveStatus.mutate({ id: project.id, data: { status: isArchived ? 'active' : 'archived' } });
  };

  const handleDelete = async () => {
    try {
      await deleteProject.mutateAsync(project.id);
      setDeleteOpen(false);
      navigate('/helpdesk/projects');
    } catch {
      setDeleteOpen(false);
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8" data-testid="project-ws-config-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mensagens ao contato */}
        <FormSection icon="message-circle" title="Mensagens ao contato" description="Avisos automáticos ao cliente">
          <NotifyContactFields
            testIdPrefix="project-ws-notify"
            contactIds={notify.contactIds}
            onContactIdsChange={(ids) => setNotify((p) => ({ ...p, contactIds: ids }))}
            channelId={notify.channelId}
            onChannelIdChange={(id) => setNotify((p) => ({ ...p, ...(id ? { channelId: id } : { channelId: undefined }) }))}
            events={notify.events}
            onToggleEvent={(key) => setNotify((p) => ({ ...p, events: { ...p.events, [key]: !p.events[key] } }))}
            messageTemplate={notify.messageTemplate}
            onMessageTemplateChange={(v) => setNotify((p) => ({ ...p, messageTemplate: v }))}
          />
          <SaveRow onSave={handleSaveNotify} loading={saveNotify.isPending} testId="project-ws-notify-save" />
        </FormSection>

        {/* Notificações internas */}
        <FormSection icon="bell" title="Notificações internas" description="Alertas para a equipe do projeto">
          <NotifyInternalFields
            testIdPrefix="project-ws-internal"
            value={internal}
            onChange={(key, val) => setInternal((p) => ({ ...p, [key]: val }))}
          />
          <SaveRow onSave={handleSaveInternal} loading={saveInternalMut.isPending} testId="project-ws-internal-save" />
        </FormSection>

        {/* Dados do projeto */}
        <FormSection icon="info" title="Dados do projeto" description="Identidade e pipeline dos tickets">
          <ProjectIdentityFields
            testIdPrefix="project-ws-identity"
            name={identity.name}
            onNameChange={(v) => setIdentity((p) => ({ ...p, name: v }))}
            clientId={identity.clientId}
            onClientIdChange={(v) => setIdentity((p) => ({ ...p, clientId: v }))}
            color={identity.color}
            onColorChange={(v) => setIdentity((p) => ({ ...p, color: v }))}
            pipelineId={identity.pipelineId}
            onPipelineChange={(v) => setIdentity((p) => ({ ...p, pipelineId: v }))}
            leaderId={identity.leaderId}
            onLeaderChange={(v) => {
              setIdentity((p) => ({ ...p, leaderId: v }));
              setMembers((prev) => prev.filter((m) => m !== v));
            }}
          />
          <SaveRow onSave={handleSaveIdentity} loading={saveIdentity.isPending} testId="project-ws-identity-save" />
        </FormSection>

        {/* Equipe */}
        <FormSection icon="users" title="Equipe" description="Membros que acompanham o projeto">
          <ProjectMembersField
            testIdPrefix="project-ws-members"
            memberIds={members}
            leaderId={identity.leaderId}
            onChange={setMembers}
          />
          <SaveRow onSave={handleSaveMembers} loading={saveMembersMut.isPending} testId="project-ws-members-save" />
        </FormSection>
      </div>

      {/* Zona de perigo */}
      <div
        className="mt-6 rounded-lg border p-6"
        style={{ borderColor: 'rgba(239, 68, 68, 0.30)', backgroundColor: 'rgba(239, 68, 68, 0.04)' }}
        data-testid="project-ws-danger-zone"
      >
        <div className="flex items-center gap-2 mb-4">
          <Icon name="alert-triangle" className="w-4 h-4" style={{ color: '#dc2626' }} />
          <h3 className="text-sm font-semibold" style={{ color: '#b91c1c' }}>
            Zona de perigo
          </h3>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
              {isArchived ? 'Reativar projeto' : 'Arquivar projeto'}
            </div>
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              {isArchived
                ? 'Volta o projeto para ativo e visível nas listagens.'
                : 'Oculta o projeto das listagens ativas. Os tickets não são afetados.'}
            </p>
          </div>
          <Button
            variant="secondary"
            icon={isArchived ? 'rotate-ccw' : 'archive'}
            onClick={handleToggleStatus}
            loading={saveStatus.isPending}
            data-testid="project-ws-toggle-status"
          >
            {isArchived ? 'Reativar' : 'Arquivar'}
          </Button>
        </div>

        <div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4 pt-4 border-t"
          style={{ borderColor: 'rgba(239, 68, 68, 0.20)' }}
        >
          <div>
            <div className="text-sm font-medium" style={{ color: '#b91c1c' }}>
              Excluir projeto
            </div>
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              Remove o projeto e o vínculo das tarefas. Não exclui os tickets do helpdesk.
            </p>
          </div>
          <Button
            variant="error"
            icon="trash"
            onClick={() => setDeleteOpen(true)}
            data-testid="project-ws-delete"
          >
            Excluir projeto
          </Button>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Excluir projeto"
        message={`Excluir o projeto "${project.name}"?`}
        warningMessage="As tarefas são desvinculadas, mas os tickets permanecem no helpdesk. Esta ação não pode ser desfeita."
        confirmButtonText="Excluir projeto"
        isLoading={deleteProject.isPending}
      />
    </div>
  );
};

ProjectConfigTab.displayName = 'ProjectConfigTab';
